import { getAllBenefitsQuery } from '@/api/benefits/benefitQueries';
import type { IngredientFormData } from '@/api/ingredients/ingredientMutations';
import {
  FALLBACK_INGREDIENT_CATEGORIES,
  getAdminIngredientCategoriesQuery,
} from '@/api/ingredients/ingredientQueries';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { MultiValueInput } from '@/components/common/MultiValueInput';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { Select } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { Tooltip } from '@/components/common/Tooltip';
import type { Supplement } from '@/domain/Supplement';
import { getStorageUrl } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

function buildIngredientFormValues(
  supplement?: Supplement | null,
): IngredientFormData {
  return {
    id: supplement?.id,
    name: supplement?.name || '',
    location: supplement?.content.location || supplement?.location || '',
    found_in: supplement?.content.found_in || '',
    form: supplement?.content.form || '',
    source: supplement?.content.source || '',
    supplier: supplement?.content.supplier || '',
    function_name: supplement?.content.function || supplement?.function || '',
    benefits_textarea: supplement?.content.benefits_textarea || '',
    doses: supplement?.content.doses || '',
    description: supplement?.content.description || '',
    product_name: supplement?.content.product_name || '',
    product_code: supplement?.content.product_code || '',
    review_date: supplement?.content.review_date || '',
    lot: supplement?.content.lot || '',
    labaratory: supplement?.content.labaratory || '',
    label_claim: supplement?.content.label_claim || '',
    result: supplement?.content.result || '',
    supplement_packaging: supplement?.content.supplement_packaging || '',
    table_items_names: supplement?.content.table_items_names || '',
    first_disclaimer: supplement?.content.first_disclaimer || '',
    lab_results_numbers: supplement?.content.lab_results_numbers || '',
    second_disclaimer: supplement?.content.second_disclaimer || '',
    articles: supplement?.content.articles || [],
    benefit_ids: supplement?.benefits.map((b) => b.id) || [],
  };
}

interface OurSupplementFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplement?: Supplement | null;
  mode: 'add' | 'edit';
  onSubmit: (data: IngredientFormData) => void;
  onDelete?: (id: number) => void;
  isPending?: boolean;
}

const packagingOptions = [
  { label: '1 Tablet', value: '1 Tablet' },
  { label: '1 Softgel capsule', value: '1 Softgel capsule' },
  { label: '1 Cellulose capsule', value: '1 Cellulose capsule' },
];

const splitCount = (s: string | undefined) =>
  (s ?? '').split(' / ').filter((part) => part.trim().length > 0).length;

const requiredNumbersCount = 3;

const supplementSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    doses: z.string().optional(),
    label_claim: z.string().optional(),
    result: z.string().optional(),
    table_items_names: z.string().optional(),
    lab_results_numbers: z.string().optional(),
  })
  .refine(
    (data) => splitCount(data.lab_results_numbers) === requiredNumbersCount,
    {
      message: `Added Numbers must have exactly ${requiredNumbersCount} values.`,
      path: ['lab_results_numbers'],
    },
  )
  .refine(
    (data) => {
      const r = splitCount(data.result);
      const l = splitCount(data.label_claim);
      const t = splitCount(data.table_items_names);
      return r === l && l === t;
    },
    {
      message:
        'Added Results, Added Claims, and Added Items must have the same amount.',
      path: ['table_items_names'],
    },
  );

export function OurSupplementFormDialog({
  open,
  onOpenChange,
  supplement,
  mode,
  onSubmit,
  onDelete,
  isPending,
}: OurSupplementFormDialogProps) {
  const [imageFile, setImageFile] = useState<File | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: benefitsResponse } = useQuery({
    ...getAllBenefitsQuery(),
    enabled: open,
  });

  const { data: categories = [...FALLBACK_INGREDIENT_CATEGORIES] } = useQuery({
    ...getAdminIngredientCategoriesQuery(),
    enabled: open,
  });

  const categoryOptions = useMemo(() => {
    const current = supplement?.content.function || supplement?.function;
    const list = [...categories];
    if (current && !list.includes(current)) {
      list.unshift(current);
    }
    return list.map((c) => ({ label: c, value: c }));
  }, [categories, supplement]);

  const allBenefits = benefitsResponse?.data || [];
  const benefitOptions = allBenefits.map((b) => ({
    id: b.id.toString(),
    value: b.text,
    label: b.text,
  }));

  const [selectedBenefitVal, setSelectedBenefitVal] = useState('');

  const formik = useFormik<IngredientFormData>({
    initialValues: buildIngredientFormValues(supplement),
    validate: toFormikValidate(supplementSchema),
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: (values) => {
      onSubmit({ ...values, image: imageFile });
    },
  });

  // Dialog stays mounted while closed, so Formik keeps dirty values unless we reset.
  useEffect(() => {
    if (!open) {
      setImageFile(undefined);
      setSelectedBenefitVal('');
      return;
    }
    formik.resetForm({ values: buildIngredientFormValues(supplement) });
  }, [open, supplement?.id]);

  const handleAddBenefit = () => {
    if (!selectedBenefitVal) return;
    const benefitId = Number(selectedBenefitVal);
    if (!isNaN(benefitId) && !formik.values.benefit_ids?.includes(benefitId)) {
      formik.setFieldValue('benefit_ids', [
        ...(formik.values.benefit_ids || []),
        benefitId,
      ]);
      setSelectedBenefitVal('');
    }
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'add' ? 'Add supplement' : 'Modify supplement'}
      maxWidth="5xl"
    >
      {open && (
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-2 lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Upload image
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex size-12 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100"
                  >
                    <Upload className="size-6 text-slate-400" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImageFile(file);
                      }
                    }}
                  />
                  {(imageFile || supplement?.image_url) && (
                    <div className="flex items-center gap-3 overflow-hidden rounded-md border border-slate-200 bg-slate-50 px-3 py-2 lg:col-span-2">
                      <div className="size-10 shrink-0 overflow-hidden rounded border border-slate-100 bg-white">
                        <img
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : getStorageUrl(supplement?.image_url || '')
                          }
                          alt="Preview"
                          className="size-full object-cover"
                        />
                      </div>
                      <span className="max-w-[200px] truncate text-xs text-slate-500">
                        {imageFile ? imageFile.name : supplement?.image_url}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Input
                id="name"
                name="name"
                label="Enter name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Input
                id="found_in"
                name="found_in"
                label="Enter found in"
                value={formik.values.found_in}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Input
                id="form"
                name="form"
                label="Enter form"
                value={formik.values.form}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Input
                id="source"
                name="source"
                label="Enter source"
                value={formik.values.source}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Input
                id="supplier"
                name="supplier"
                label="Enter supplier"
                value={formik.values.supplier}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Input
                id="location"
                name="location"
                label="Enter final location of manufacturing"
                value={formik.values.location}
                onChange={formik.handleChange}
                className="bg-white"
              />

              <Select
                options={categoryOptions}
                value={formik.values.function_name || ''}
                onChange={(value) =>
                  formik.setFieldValue('function_name', value)
                }
                label="Category"
                placeholder="Select category..."
                className="bg-white"
              />

              <div className="flex flex-col gap-4 lg:col-span-2">
                <MultiValueInput
                  type="number"
                  label="Enter doses"
                  placeholder="e.g. 1 Tablet"
                  maxItems={999}
                  value={
                    formik.values.doses
                      ? formik.values.doses.split(' / ').filter(Boolean)
                      : []
                  }
                  onChange={(list) =>
                    formik.setFieldValue('doses', list.join(' / '))
                  }
                  error={
                    formik.touched.doses && formik.errors.doses
                      ? formik.errors.doses
                      : undefined
                  }
                  addedItemsTitle="Added Doses"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Textarea
                id="description"
                name="description"
                label="Enter supplement description"
                value={formik.values.description}
                onChange={formik.handleChange}
                className="min-h-[120px] bg-white"
              />
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">
                Certificate of Analysis (CoA)
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                  id="product_name"
                  name="product_name"
                  label="Enter product name"
                  value={formik.values.product_name}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <Input
                  id="product_code"
                  name="product_code"
                  label="Enter product code"
                  value={formik.values.product_code}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <Input
                  id="review_date"
                  name="review_date"
                  label="Enter review date"
                  value={formik.values.review_date}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <Input
                  id="lot"
                  name="lot"
                  label="Enter lot#"
                  value={formik.values.lot}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <Select
                  id="supplement_packaging"
                  name="supplement_packaging"
                  label="Select supplement form"
                  options={packagingOptions}
                  value={formik.values.supplement_packaging}
                  onChange={(val) =>
                    formik.setFieldValue('supplement_packaging', val)
                  }
                  className="bg-white"
                />
                <Input
                  id="first_disclaimer"
                  name="first_disclaimer"
                  label="Enter quality method"
                  value={formik.values.first_disclaimer}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <Input
                  id="second_disclaimer"
                  name="second_disclaimer"
                  label="Enter microbial analysis method"
                  value={formik.values.second_disclaimer}
                  onChange={formik.handleChange}
                  className="bg-white"
                />
                <div className="flex flex-col gap-4 lg:col-span-2">
                  <MultiValueInput
                    label="Enter lab results numbers"
                    placeholder="e.g. 123"
                    type="text"
                    maxItems={3}
                    value={
                      formik.values.lab_results_numbers
                        ? formik.values.lab_results_numbers
                            .split(' / ')
                            .filter(Boolean)
                        : []
                    }
                    onChange={(list) =>
                      formik.setFieldValue(
                        'lab_results_numbers',
                        list.join(' / '),
                      )
                    }
                    addedItemsTitle="Added Numbers"
                    error={
                      formik.errors.lab_results_numbers
                        ? formik.errors.lab_results_numbers
                        : undefined
                    }
                  />
                </div>
                <div className="flex flex-col gap-4 lg:col-span-2">
                  <MultiValueInput
                    label="Enter result"
                    placeholder="e.g. Result"
                    maxItems={999}
                    value={
                      formik.values.result
                        ? formik.values.result.split(' / ').filter(Boolean)
                        : []
                    }
                    onChange={(list) =>
                      formik.setFieldValue('result', list.join(' / '))
                    }
                    addedItemsTitle="Added Results"
                    error={
                      formik.errors.result ? formik.errors.result : undefined
                    }
                  />
                </div>
                <div className="flex flex-col gap-4 lg:col-span-2">
                  <MultiValueInput
                    label="Enter label claim"
                    placeholder="e.g. Claim"
                    maxItems={999}
                    value={
                      formik.values.label_claim
                        ? formik.values.label_claim.split(' / ').filter(Boolean)
                        : []
                    }
                    onChange={(list) =>
                      formik.setFieldValue('label_claim', list.join(' / '))
                    }
                    addedItemsTitle="Added Claims"
                    error={
                      formik.errors.label_claim
                        ? formik.errors.label_claim
                        : undefined
                    }
                  />
                </div>
                <div className="flex flex-col gap-4 lg:col-span-2">
                  <MultiValueInput
                    label="Enter items names"
                    placeholder="e.g. Item Name"
                    maxItems={999}
                    value={
                      formik.values.table_items_names
                        ? formik.values.table_items_names
                            .split(' / ')
                            .filter(Boolean)
                        : []
                    }
                    onChange={(list) =>
                      formik.setFieldValue(
                        'table_items_names',
                        list.join(' / '),
                      )
                    }
                    addedItemsTitle="Added Items"
                    error={
                      formik.errors.table_items_names
                        ? formik.errors.table_items_names
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">
                Benefits Added
              </h3>
              <div className="mb-4 flex flex-col gap-2">
                <div className="mb-1 grid grid-cols-[1fr_auto] gap-4">
                  <span className="text-sm font-medium text-slate-700">
                    Benefit Name
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {formik.values.benefit_ids?.map((benefitId) => {
                    const benefit = allBenefits.find((b) => b.id === benefitId);
                    return (
                      <div
                        key={benefitId}
                        className="flex items-center justify-between border-b border-slate-50 py-2"
                      >
                        <span className="text-sm text-slate-600">
                          {benefit?.text || 'Unknown Benefit'}
                        </span>
                        <RelationActionButton
                          type="button"
                          onClick={() => {
                            formik.setFieldValue(
                              'benefit_ids',
                              formik.values.benefit_ids?.filter(
                                (id) => id !== benefitId,
                              ),
                            );
                          }}
                          className="text-delete"
                        >
                          Remove
                        </RelationActionButton>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex w-full max-w-md flex-col gap-4">
                <Combobox
                  label="Benefits (Select from the list)"
                  items={benefitOptions}
                  value={selectedBenefitVal}
                  onChange={(val) => setSelectedBenefitVal(val || '')}
                  className="bg-white"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-fit px-8"
                  onClick={handleAddBenefit}
                >
                  Add
                </Button>
              </div>
              <div className="mt-8">
                <Textarea
                  id="benefits_textarea"
                  name="benefits_textarea"
                  label="Benefits Right Textarea"
                  value={formik.values.benefits_textarea}
                  onChange={formik.handleChange}
                  className="min-h-[100px] bg-white"
                />
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">
                Studies
              </h3>
              <FieldArray name="articles">
                {({ push, remove }) => (
                  <div className="flex flex-col gap-6">
                    {formik.values.articles?.map((article, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-4 rounded-lg bg-gray-100 p-4"
                      >
                        <Input
                          name={`articles.${index}.source`}
                          label="Sources of studies"
                          value={article.source}
                          onChange={formik.handleChange}
                          className="bg-white"
                        />
                        <Textarea
                          name={`articles.${index}.description`}
                          label="Content"
                          value={article.description}
                          onChange={formik.handleChange}
                          className="bg-white"
                        />
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => remove(index)}
                          className="self-start"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <div className="flex flex-col gap-4 rounded-lg bg-slate-100 p-4">
                      <h4 className="font-medium text-slate-700">
                        Add new article:
                      </h4>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => push({ source: '', description: '' })}
                        className="self-start"
                      >
                        Add New Source
                      </Button>
                    </div>
                  </div>
                )}
              </FieldArray>
            </div>

            <div className="flex items-center gap-4">
              <Tooltip
                content="Added Numbers must have exactly 3 values, and Added Results, Added Claims, and Added Items must have the same amount."
                enabled={!formik.isValid}
              >
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    disabled={isPending || !formik.isValid}
                  >
                    {mode === 'add' ? 'Create Supplement' : 'Save Changes'}
                  </Button>
                </div>
              </Tooltip>
              {mode === 'edit' && supplement?.id && onDelete && (
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={() => onDelete(supplement.id)}
                  className="bg-delete text-white"
                >
                  Delete Item
                </Button>
              )}
            </div>
          </form>
        </FormikProvider>
      )}
    </DialogWrapper>
  );
}
